import { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';

const STORAGE_KEY = 'sync-drawer-width';
const MIN_WIDTH = 400;
const MAX_WIDTH_RATIO = 0.9;

export default function CompanyDrawer({ company, onClose, children }) {
  const [width, setWidth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(STORAGE_KEY), 10);
      return saved && saved >= MIN_WIDTH ? saved : 640;
    } catch { return 640; }
  });
  const [activeTab, setActiveTab] = useState('gl');
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  // Escape to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Drag resize
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = width;
  }, [width]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const maxW = window.innerWidth * MAX_WIDTH_RATIO;
      const delta = startX.current - e.clientX;
      const newW = Math.min(maxW, Math.max(MIN_WIDTH, startW.current + delta));
      setWidth(newW);
    };
    const onMouseUp = () => {
      if (dragging.current) {
        dragging.current = false;
        try { localStorage.setItem(STORAGE_KEY, String(Math.round(width))); } catch {}
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [width]);

  const tabs = [
    { key: 'gl', label: 'GL Sync' },
    { key: 'entity', label: 'Entity Sync' },
    { key: 'reconcile', label: 'Reconciliation' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className="relative bg-gray-900 border-l border-gray-700/50 shadow-2xl flex flex-col h-full"
        style={{ width: `${width}px` }}
      >
        {/* Drag handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-blue-500/30 transition-colors z-10"
          onMouseDown={onMouseDown}
        />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div>
            <h2 className="text-base font-semibold text-gray-100">{company.display_name || company.rf_id}</h2>
            <p className="text-[11px] text-gray-500 font-mono mt-0.5">{company.rf_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1.5 rounded-lg hover:bg-gray-800 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800 px-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-400 text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {children(activeTab)}
        </div>
      </div>
    </div>
  );
}
